import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {Plus, Edit, Trash2, Save, X, GripVertical, Eye, EyeOff} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface ProductCategory {
  _id: string
  value: string
  label: string
  labelEn: string
  description?: string
  icon?: string
  sortOrder: number
  status: 'active' | 'inactive'
  createdAt: string
  updatedAt: string
}

const ProductCategoryManager: React.FC = () => {
  const [categories, setCategories] = useState<ProductCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [formData, setFormData] = useState<Partial<ProductCategory>>({
    value: '',
    label: '',
    labelEn: '',
    description: '',
    sortOrder: 0,
    status: 'active'
  })

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    setLoading(true)
    try {
      const response = await lumi.entities.product_categories.list({
        sort: { sortOrder: 1 }
      })
      setCategories(response.list || [])
    } catch (error) {
      console.error('Failed to fetch categories:', error)
      toast.error('載入分類失敗')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setIsAdding(true)
    setEditingId(null)
    setFormData({
      value: '',
      label: '',
      labelEn: '',
      description: '',
      sortOrder: categories.length + 1,
      status: 'active'
    })
  }

  const handleEdit = (category: ProductCategory) => {
    setEditingId(category._id)
    setIsAdding(false)
    setFormData({
      value: category.value,
      label: category.label,
      labelEn: category.labelEn,
      description: category.description || '',
      sortOrder: category.sortOrder,
      status: category.status
    })
  }

  const handleCancel = () => {
    setEditingId(null)
    setIsAdding(false)
    setFormData({
      value: '',
      label: '',
      labelEn: '',
      description: '',
      sortOrder: 0,
      status: 'active'
    })
  }

  const handleSave = async () => {
    if (!formData.value || !formData.label || !formData.labelEn) {
      toast.error('請填寫必填欄位')
      return
    }

    try {
      const now = new Date().toISOString()
      const data = {
        ...formData,
        updatedAt: now
      }

      if (isAdding) {
        await lumi.entities.product_categories.create({
          ...data,
          createdAt: now
        })
        toast.success('分類新增成功')
      } else if (editingId) {
        await lumi.entities.product_categories.update(editingId, data)
        toast.success('分類更新成功')
      }

      handleCancel()
      fetchCategories()
    } catch (error) {
      console.error('Save failed:', error)
      toast.error('儲存失敗')
    }
  }

  const handleDelete = async (categoryId: string, categoryValue: string) => {
    if (!confirm(`確定要刪除分類「${categoryValue}」嗎？\n\n注意：刪除後可能影響使用此分類的商品。`)) {
      return
    }

    try {
      await lumi.entities.product_categories.delete(categoryId)
      toast.success('分類刪除成功')
      fetchCategories()
    } catch (error) {
      console.error('Delete failed:', error)
      toast.error('刪除失敗')
    }
  }

  const toggleStatus = async (category: ProductCategory) => {
    const newStatus = category.status === 'active' ? 'inactive' : 'active'
    try {
      await lumi.entities.product_categories.update(category._id, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      })
      toast.success(`分類已${newStatus === 'active' ? '啟用' : '停用'}`)
      fetchCategories()
    } catch (error) {
      console.error('Toggle status failed:', error)
      toast.error('操作失敗')
    }
  }

  const handleSortOrderChange = async (categoryId: string, newOrder: number) => {
    try {
      await lumi.entities.product_categories.update(categoryId, {
        sortOrder: newOrder,
        updatedAt: new Date().toISOString()
      })
      fetchCategories()
    } catch (error) {
      console.error('Update sort order failed:', error)
      toast.error('排序更新失敗')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
      </div>
    )
  }

  return (
    <div className="p-8 h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-light tracking-wider mb-2">商品分類管理</h1>
            <p className="text-gray-400">管理商品分類、排序與狀態</p>
          </div>
          {!isAdding && !editingId && (
            <button
              onClick={handleAdd}
              className="flex items-center px-6 py-3 bg-gray-400 text-black hover:bg-gray-300 transition-colors duration-200 text-sm font-light tracking-wider"
            >
              <Plus size={16} className="mr-2" />
              新增分類
            </button>
          )}
        </div>

        {/* Add/Edit Form */}
        {(isAdding || editingId) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 bg-gray-950 border border-gray-700"
          >
            <h3 className="text-xl font-light tracking-wider mb-4">
              {isAdding ? '新增分類' : '編輯分類'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  分類代碼 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder="例：beans（英文小寫）"
                  className="w-full bg-gray-900 border border-gray-700 px-4 py-2 text-gray-100 placeholder-gray-600 focus:border-gray-500 focus:outline-none"
                  disabled={!isAdding}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  排序順序 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                  className="w-full bg-gray-900 border border-gray-700 px-4 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  分類名稱（中文）<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="例：咖啡豆"
                  className="w-full bg-gray-900 border border-gray-700 px-4 py-2 text-gray-100 placeholder-gray-600 focus:border-gray-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  分類名稱（英文）<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.labelEn}
                  onChange={(e) => setFormData({ ...formData, labelEn: e.target.value })}
                  placeholder="例：Coffee Beans"
                  className="w-full bg-gray-900 border border-gray-700 px-4 py-2 text-gray-100 placeholder-gray-600 focus:border-gray-500 focus:outline-none"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-2">描述</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="分類描述（可選）"
                  rows={2}
                  className="w-full bg-gray-900 border border-gray-700 px-4 py-2 text-gray-100 placeholder-gray-600 focus:border-gray-500 focus:outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">狀態</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                  className="w-full bg-gray-900 border border-gray-700 px-4 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                >
                  <option value="active">啟用</option>
                  <option value="inactive">停用</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="flex items-center px-4 py-2 bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors text-sm"
              >
                <X size={16} className="mr-2" />
                取消
              </button>
              <button
                onClick={handleSave}
                className="flex items-center px-4 py-2 bg-gray-400 text-black hover:bg-gray-300 transition-colors text-sm"
              >
                <Save size={16} className="mr-2" />
                儲存
              </button>
            </div>
          </motion.div>
        )}

        {/* Categories List */}
        <div className="space-y-3">
          {categories.map((category) => (
            <motion.div
              key={category._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-950 border border-gray-800 p-4 hover:border-gray-700 transition-colors"
            >
              <div className="flex items-center justify-between">
                {/* Left: Drag Handle + Info */}
                <div className="flex items-center space-x-4 flex-1">
                  <div className="text-gray-600 cursor-move" title="拖曳排序">
                    <GripVertical size={20} />
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="number"
                      value={category.sortOrder}
                      onChange={(e) => handleSortOrderChange(category._id, parseInt(e.target.value) || 0)}
                      className="w-16 bg-gray-900 border border-gray-700 px-2 py-1 text-sm text-gray-100 text-center focus:border-gray-500 focus:outline-none"
                      title="排序順序"
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-1">
                      <span className="text-gray-100 font-light tracking-wide">
                        {category.label}
                      </span>
                      <span className="text-gray-500 text-sm">
                        {category.labelEn}
                      </span>
                      <span className="px-2 py-0.5 bg-gray-800 text-gray-400 text-xs font-mono">
                        {category.value}
                      </span>
                    </div>
                    {category.description && (
                      <p className="text-sm text-gray-500">{category.description}</p>
                    )}
                  </div>
                </div>

                {/* Right: Status + Actions */}
                <div className="flex items-center space-x-3">
                  <span className={`px-3 py-1 text-xs font-light tracking-wider ${
                    category.status === 'active' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    {category.status === 'active' ? '啟用' : '停用'}
                  </span>

                  <button
                    onClick={() => toggleStatus(category)}
                    className="p-2 text-gray-400 hover:text-gray-100 transition-colors"
                    title={category.status === 'active' ? '停用分類' : '啟用分類'}
                  >
                    {category.status === 'active' ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>

                  <button
                    onClick={() => handleEdit(category)}
                    className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                    title="編輯分類"
                  >
                    <Edit size={16} />
                  </button>

                  <button
                    onClick={() => handleDelete(category._id, category.label)}
                    className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                    title="刪除分類"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {categories.length === 0 && (
          <div className="text-center py-12 bg-gray-950 border border-gray-800">
            <p className="text-gray-400">尚無分類資料，請新增第一個分類</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductCategoryManager
